import React, { useState, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate, Link } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";

import { api } from "../api";
import "../assets/styles/Auth.css";

export default function Register() {
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);

  const [captcha, setCaptcha] = useState("");
  const [loading, setLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState(false);

  const [success, setSuccess] = useState("");
  const [serverError, setServerError] = useState("");

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});

  // Handle Input Change
  const handleChange = (e) => {
    let { name, value } = e.target;

    // Remove leading spaces except passwords
    if (
      name !== "password" &&
      name !== "confirmPassword"
    ) {
      value = value.replace(/^\s+/, "");
    }

    // Allow only digits for mobile
    if (name === "mobile") {
      value = value.replace(/\D/g, "");
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear field error while typing
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    setServerError("");
    setSuccess("");
  };

  // Validation
  const validate = () => {
    const err = {};

    // First Name
    if (!form.firstName.trim()) {
      err.firstName = "First name is required";
    }

    // Last Name
    if (!form.lastName.trim()) {
      err.lastName = "Last name is required";
    }

    // Email
    if (!form.email.trim()) {
      err.email = "Email is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(
        form.email.trim()
      )
    ) {
      err.email = "Invalid email format";
    }

    // Mobile
    if (!form.mobile.trim()) {
      err.mobile = "Mobile number is required";
    } else if (
      !/^[0-9]{10}$/.test(form.mobile.trim())
    ) {
      err.mobile =
        "Enter a valid 10 digit mobile number";
    }

    // Password
    if (!form.password) {
      err.password = "Password is required";
    } else if (form.password.length < 6) {
      err.password =
        "Password must be at least 6 characters";
    }

    // Confirm Password
    if (!form.confirmPassword) {
      err.confirmPassword =
        "Confirm your password";
    } else if (
      form.password !== form.confirmPassword
    ) {
      err.confirmPassword =
        "Passwords do not match";
    }

    // CAPTCHA
    if (!captcha) {
      err.captcha = "Please verify CAPTCHA";
    }

    setErrors(err);

    return Object.keys(err).length === 0;
  };

  
      // Register
  const handleRegister = async (e) => {
    e.preventDefault();

    if (loading) return;

    setSuccess("");
    setServerError("");

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      const res = await api.register({
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        email: form.email.trim().toLowerCase(),
        mobile: form.mobile.trim(),
        password: form.password,
        captcha,
      });

      if (res.status === "success") {
        setSuccess(
          "Registration successful! Redirecting to login..."
        );

        setForm({
          firstName: "",
          lastName: "",
          email: "",
          mobile: "",
          password: "",
          confirmPassword: "",
        });

        setErrors({});
        setCaptcha("");
        recaptchaRef.current?.reset();

        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else {
        setServerError(
          res.message || "Registration failed"
        );

        setCaptcha("");
        recaptchaRef.current?.reset();
      }
    } catch (error) {
  console.error(error);

  setServerError(
    error?.response?.data?.message ||
    "Something went wrong. Please try again."
  );

  setCaptcha("");
  recaptchaRef.current?.reset();
} finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-wrapper">
      <div className="support-banner">
        <h1>Remote Desktop Support</h1>
      </div>

      <form
        className="auth-card"
        autoComplete="off"
        noValidate
        onSubmit={handleRegister}
      >
        <h2>Create Account</h2>

        {success && (
          <div className="success-message">
            {success}
          </div>
        )}

        {serverError && (
          <div className="error-message">
            {serverError}
          </div>
        )}

        <div className="form-grid">
            {/* First Name */}
          <div className="form-control">
            <div className="lab">
              <label>
                First Name <span className="req">*</span>
              </label>
            </div>

            <input
              type="text"
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              autoComplete="given-name"
              spellCheck={false}
              maxLength={50}
              required
            />

            <span className="error">{errors.firstName}</span>
          </div>

          {/* Last Name */}
          <div className="form-control">
            <div className="lab">
              <label>
                Last Name <span className="req">*</span>
              </label>
            </div>

            <input
              type="text"
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              autoComplete="family-name"
              spellCheck={false}
              maxLength={50}
              required
            />

            <span className="error">{errors.lastName}</span>
          </div>

          {/* Email */}
          <div className="form-control">
            <div className="lab">
              <label>
                Email <span className="req">*</span>
              </label>
            </div>

            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              maxLength={100}
              required
            />

            <span className="error">{errors.email}</span>
          </div>

          {/* Mobile */}
          <div className="form-control">
            <div className="lab">
              <label>
                Mobile <span className="req">*</span>
              </label>
            </div>

            <input
              type="tel"
              name="mobile"
              value={form.mobile}
              onChange={handleChange}
              autoComplete="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={10}
              required
              onKeyDown={(e) => {
                if (
                  !/[0-9]/.test(e.key) &&
                  ![
                    "Backspace",
                    "Delete",
                    "ArrowLeft",
                    "ArrowRight",
                    "Home",
                    "End",
                    "Tab",
                  ].includes(e.key)
                ) {
                  e.preventDefault();
                }
              }}
              onPaste={(e) => {
                const paste = e.clipboardData.getData("text");

                if (!/^\d+$/.test(paste)) {
                  e.preventDefault();
                }
              }}
            />

            <span className="error">{errors.mobile}</span>
          </div>

          {/* Password */}
<div className="form-control">
  <div className="lab">
    <label>
      Password <span className="req">*</span>
    </label>
  </div>

  <div className="password-field">
    <input
      type={showPassword ? "text" : "password"}
      name="password"
      value={form.password}
      onChange={handleChange}
      autoComplete="new-password"
      required
    />

    <span
      className="eye"
      onClick={() => setShowPassword((prev) => !prev)}
    >
      {showPassword ? <FaEye /> : <FaEyeSlash />}
    </span>
  </div>

  <span className="error">{errors.password}</span>
</div>

          {/* Confirm Password */}
<div className="form-control">
  <div className="lab">
    <label>
      Confirm Password <span className="req">*</span>
    </label>
  </div>

  <div className="password-field">
    <input
      type={showConfirmPassword ? "text" : "password"}
      name="confirmPassword"
      value={form.confirmPassword}
      onChange={handleChange}
      autoComplete="new-password"
      required
    />

    <span
      className="eye"
      onClick={() =>
        setShowConfirmPassword((prev) => !prev)
      }
    >
      {showConfirmPassword ? (
        <FaEye />
      ) : (
        <FaEyeSlash />
      )}
    </span>
  </div>

  <span className="error">
    {errors.confirmPassword}
  </span>
</div>

            {/* CAPTCHA */}
          <div className="form-control full">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey="6LfGasIsAAAAAGQiyk-Gp8R4DMJnYysExBm02-bw"
              onChange={(value) => {
                setCaptcha(value || "");

                setErrors((prev) => ({
                  ...prev,
                  captcha: "",
                }));
              }}
              onExpired={() => {
                setCaptcha("");

                setErrors((prev) => ({
                  ...prev,
                  captcha: "CAPTCHA expired. Please verify again.",
                }));

                recaptchaRef.current?.reset();
              }}
            />

            <span className="error">{errors.captcha}</span>
          </div>
        </div>

        <button
          type="submit"
          className="auth-btn"
          disabled={loading}
        >
          {loading ? "Creating Account..." : "Register"}
        </button>

        <p className="switch-text">
          Already have an account?{" "}
          <Link to="/">Login</Link>
        </p>
      </form>
    </div>
  );
}